import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { messageContext } from "../../../../stores/messages/messageContext";
import {
  autoDestinationUrl,
  calendarClearUrl,
  calendarFillUrl,
  calendarSettingsUrl,
  calendarSkipUrl,
  calendarUrl,
  getUserTranscriptVideosUrl,
} from "../../../constants";
import { CALENDAR_TEMPLATES, CALENDAR_TEXT as T } from "../constants/calendarText";
import { VideoData } from "../types";
import Dialog from "@mui/material/Dialog";
import "./CalendarTab.css";

/** One booked post (transcript/posting_calendar.py calendar_view). */
interface CalendarPost {
  id: number;
  post_at: string;
  state: "rendering" | "ready" | "posted" | "failed" | "skipped" | "scheduled";
  text: string;
  transcript_id: number | null;
  creation_id: number | null;
  error: string;
  /** The video it comes from, the clip's poster, and the finished video once it's cut. */
  title?: string;
  thumbnail?: string;
  video?: string;
}

interface CalendarData {
  schedule: { times: string[]; timezone: string; paused?: boolean };
  /** "AI picks, on us" — the monthly allowance (posting_calendar.ai_allowance). */
  ai?: { allowed: number | null; used: number; left: number | null; available: boolean; running: boolean };
  last_ai_fill?: { id: number; status: string; booked: number; message: string } | null;
  days: number;
  slots: string[];
  empty_slots: number;
  posts: CalendarPost[];
  /** Where the posts go: the account's Auto (ready, now/review, private/public). */
  auto?: { ready: boolean; post_when: string; privacy: string };
}

const DAYS = 14;
const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** The AI virality scores the editor saved for a video (createVideo.jsx cc_virality_<id>), best first. */
export const readScores = (transcriptId: number): number[] | null => {
  try {
    const raw = localStorage.getItem(`cc_virality_${transcriptId}`);
    if (!raw) return null;
    const pairs = JSON.parse(raw) as [string | number, number][];
    if (!Array.isArray(pairs) || !pairs.length) return null;
    return pairs
      .filter((p) => Array.isArray(p) && /^\d+$/.test(String(p[0])))
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map((p) => Number(p[0]));
  } catch {
    return null;
  }
};

/** Several videos take turns: [[a1, a2], [b1]] → [a1, b1, a2]. */
export const interleave = <X,>(lists: X[][]): X[] => {
  const out: X[] = [];
  const longest = Math.max(0, ...lists.map((l) => l.length));
  for (let i = 0; i < longest; i += 1) lists.forEach((l) => i < l.length && out.push(l[i]));
  return out;
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const dayLabel = (d: Date) => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (dayKey(d) === dayKey(today)) return T.TODAY;
  if (dayKey(d) === dayKey(tomorrow)) return T.TOMORROW;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const timeLabel = (d: Date) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/**
 * My Library → Calendar. Posting times, a Fill button that books a clip into every empty
 * slot (free templates, or the user's own AI scores), and the next two weeks day by day.
 * Kerem Green House: "click a few buttons and not need anything for the next two weeks".
 */
const CalendarTab = () => {
  const messageStore = useContext(messageContext);
  const [data, setData] = useState<CalendarData | null>(null);
  const [error, setError] = useState("");
  const [hasAuto, setHasAuto] = useState(true);
  const [newTime, setNewTime] = useState("12:00");
  const [fillOpen, setFillOpen] = useState(false);
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [picker, setPicker] = useState<"template" | "order" | "ai">("template");
  const [recipe, setRecipe] = useState<string>(CALENDAR_TEMPLATES[0].key);
  const [search, setSearch] = useState("");
  const [booking, setBooking] = useState(false);
  const [opened, setOpened] = useState<CalendarPost | null>(null);
  const [ahead, setAhead] = useState(DAYS);

  const load = useCallback(async () => {
    try {
      const response = await apiRequest(`${calendarUrl}?days=${DAYS}`, { method: "GET" });
      if (!response.ok) throw new Error(String(response.status));
      setData(await response.json());
      setError("");
    } catch {
      setError(T.LOAD_ERROR);
    }
  }, []);

  const saveSchedule = useCallback(async (body: { times?: string[]; timezone?: string; paused?: boolean }) => {
    try {
      const response = await apiRequest(calendarSettingsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch((): null => null);
      if (!response.ok) {
        messageStore.setMessage(result?.message || T.SAVE_ERROR, "error", 6000);
        return;
      }
      await load();
    } catch {
      messageStore.setMessage(T.SAVE_ERROR, "error", 6000);
    }
  }, [load, messageStore]);

  useEffect(() => {
    load();
    apiRequest(autoDestinationUrl, { method: "GET" })
      .then((r): Promise<any> | null => (r.ok ? r.json() : null))
      .then((d) => setHasAuto(Boolean(d?.destination?.url && d?.destination?.enabled)))
      .catch(() => {});
  }, [load]);

  // Times follow this computer's time zone — tried once, so a zone the server doesn't
  // know (it falls back to UTC) can't loop.
  const tzSynced = useRef(false);
  useEffect(() => {
    const tz = browserTimezone();
    if (!data || tzSynced.current) return;
    tzSynced.current = true;
    if (data.schedule.timezone !== tz) saveSchedule({ timezone: tz });
  }, [data, saveSchedule]);

  useEffect(() => {
    if (!fillOpen || videos.length) return;
    apiRequest(getUserTranscriptVideosUrl, { method: "GET" })
      .then((r): Promise<VideoData[]> | VideoData[] => (r.ok ? r.json() : []))
      .then((list: VideoData[]) => setVideos((list || []).filter((v) => v.id)))
      .catch(() => {});
  }, [fillOpen, videos.length]);

  // While an AI fill runs in the worker, check back every 8 s; say how it went once.
  const aiRunning = Boolean(data?.ai?.running);
  // True from the moment this page starts an AI fill until its result has been shown.
  const waitingForAi = useRef(false);
  useEffect(() => {
    if (!aiRunning) return undefined;
    const timer = window.setInterval(load, 8000);
    return () => window.clearInterval(timer);
  }, [aiRunning, load]);
  useEffect(() => {
    const last = data?.last_ai_fill;
    if (!waitingForAi.current || !last || last.status === "running") return;
    waitingForAi.current = false;
    messageStore.setMessage(last.message, last.status === "done" ? "success" : "error", 8000);
  }, [data, messageStore]);

  // While clips are being cut, look again every 10 s so Cutting… turns into Ready by itself.
  const cutting = Boolean(data?.posts.some((p) => p.state === "rendering"));
  useEffect(() => {
    if (!cutting || aiRunning) return undefined;
    const timer = window.setInterval(load, 10000);
    return () => window.clearInterval(timer);
  }, [cutting, aiRunning, load]);

  // Every slot and every booked post, grouped by local day.
  const days = useMemo(() => {
    if (!data) return [] as { key: string; label: string; cells: { at: Date; post?: CalendarPost }[] }[];
    const byTime = new Map<number, { at: Date; post?: CalendarPost }>();
    data.slots.forEach((iso) => byTime.set(new Date(iso).getTime(), { at: new Date(iso) }));
    data.posts.forEach((post) => {
      const at = new Date(post.post_at);
      byTime.set(at.getTime(), { at, post });
    });
    const groups = new Map<string, { key: string; label: string; cells: { at: Date; post?: CalendarPost }[] }>();
    Array.from(byTime.values())
      .sort((a, b) => a.at.getTime() - b.at.getTime())
      .forEach((cell) => {
        const key = dayKey(cell.at);
        if (!groups.has(key)) groups.set(key, { key, label: dayLabel(cell.at), cells: [] });
        groups.get(key)!.cells.push(cell);
      });
    return Array.from(groups.values());
  }, [data]);

  const booked = data ? data.posts.filter((p) => p.state !== "skipped" && new Date(p.post_at) > new Date()).length : 0;
  const shownVideos = videos.filter((v) => (v.title || "").toLowerCase().includes(search.trim().toLowerCase())).slice(0, 40);
  const chosen = videos.filter((v) => selected.has(v.id));
  const missingScores = picker === "order" ? chosen.filter((v) => !readScores(v.id)) : [];

  // Opening Fill: AI picks is the first choice whenever there are AI picks left.
  const openFill = () => {
    setFillOpen((open) => {
      const ai = data?.ai;
      if (!open && ai?.available && ai.left !== 0) setPicker("ai");
      return !open;
    });
  };
  const nextPost = data
    ? data.posts.filter((p) => p.state !== "skipped" && p.state !== "posted" && new Date(p.post_at) > new Date())
      .sort((a, b) => new Date(a.post_at).getTime() - new Date(b.post_at).getTime())[0]
    : undefined;

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const doFill = async () => {
    if (!chosen.length) {
      messageStore.setMessage(T.PICK_A_VIDEO, "error", 5000);
      return;
    }
    setBooking(true);
    try {
      const body: Record<string, unknown> = { transcript_ids: chosen.map((v) => v.id), picker, days: ahead };
      if (picker === "template") body.recipe = recipe;
      else if (picker === "order") body.clip_ids = interleave(chosen.map((v) => readScores(v.id) || []));
      const response = await apiRequest(calendarFillUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch((): null => null);
      messageStore.setMessage(result?.message || T.FILL_ERROR, response.ok ? "success" : "error", 8000);
      // An AI fill finishes in the worker: announce its result when it lands.
      if (response.status === 202) waitingForAi.current = true;
      if (response.ok) {
        setFillOpen(false);
        await load();
      }
    } catch {
      messageStore.setMessage(T.FILL_ERROR, "error", 6000);
    } finally {
      setBooking(false);
    }
  };

  const doSkip = async (post: CalendarPost) => {
    const response = await apiRequest(calendarSkipUrl(post.id), { method: "POST" }).catch((): null => null);
    if (response?.ok) {
      setOpened(null);
      await load();
    }
  };

  const doClear = async () => {
    if (!window.confirm(T.clearConfirm(booked))) return;
    const response = await apiRequest(calendarClearUrl, { method: "POST" }).catch((): null => null);
    const result = response ? await response.json().catch((): null => null) : null;
    messageStore.setMessage(result?.message || T.CLEAR_ERROR, response?.ok ? "success" : "error", 6000);
    if (response?.ok) await load();
  };

  // Empty slots within the chosen reach (the server books exactly these).
  const emptyWithin = (days: number) => {
    if (!data) return 0;
    const until = Date.now() + days * 24 * 3600 * 1000;
    const taken = new Set(data.posts.filter((p) => p.state !== "skipped" && p.state !== "failed").map((p) => new Date(p.post_at).getTime()));
    return data.slots.filter((iso) => {
      const t = new Date(iso).getTime();
      return t <= until && !taken.has(t);
    }).length;
  };

  if (error) return <p className="cc-cal-note">{error}</p>;
  if (!data) return <p className="cc-cal-note">…</p>;

  return (
    <div className="cc-cal">
      <div className="cc-cal-steps" aria-label={T.STEPS_TITLE}>
        <div className="cc-cal-step"><span className="cc-cal-step-n">1</span>{T.STEP_PICK}</div>
        <div className="cc-cal-step"><span className="cc-cal-step-n">2</span>{T.STEP_CUT}</div>
        <div className="cc-cal-step"><span className="cc-cal-step-n">3</span>{T.stepPost(data.auto?.privacy || "")}</div>
      </div>
      {!hasAuto ? <p className="cc-cal-warning">{T.NEEDS_AUTO}</p> : null}

      <div className="cc-cal-times">
        <span className="cc-cal-label">{T.TIMES}</span>
        {data.schedule.times.map((t) => (
          <span key={t} className="cc-cal-time">
            {t}
            {data.schedule.times.length > 1 ? (
              <button
                type="button"
                aria-label={T.removeTime(t)}
                title={T.removeTime(t)}
                onClick={() => saveSchedule({ times: data.schedule.times.filter((x) => x !== t) })}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        <input
          id="cc-cal-new-time"
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          aria-label={T.ADD_TIME}
        />
        <button type="button" className="cc-cal-ghost" onClick={() => saveSchedule({ times: [...data.schedule.times, newTime] })}>
          {T.ADD_TIME}
        </button>
        <span className="cc-cal-tz">{T.timezone(data.schedule.timezone)}</span>
      </div>

      <div className={`cc-cal-bar${data.schedule.paused ? " is-paused" : ""}`}>
        <span className="cc-cal-summary">
          {aiRunning ? T.AI_RUNNING : T.summary(booked, data.empty_slots, data.days)}
          {!aiRunning && nextPost && !data.schedule.paused ? T.nextPost(`${dayLabel(new Date(nextPost.post_at))} ${timeLabel(new Date(nextPost.post_at))}`) : ""}
          {data.schedule.paused ? <span className="cc-cal-paused">{T.PAUSED_NOTE}</span> : null}
        </span>
        <span className="cc-cal-controls">
          <button
            type="button"
            className="cc-cal-ghost"
            title={T.PAUSE_TITLE}
            aria-pressed={Boolean(data.schedule.paused)}
            onClick={() => saveSchedule({ paused: !data.schedule.paused })}
          >
            {data.schedule.paused ? T.RESUME : T.PAUSE}
          </button>
          <button type="button" className="cc-cal-ghost" disabled={!booked} onClick={doClear}>{T.CLEAR}</button>
        </span>
        <button
          type="button"
          className="cc-cal-primary"
          disabled={!data.empty_slots}
          onClick={openFill}
        >
          {data.empty_slots ? T.fill(data.empty_slots) : T.FILL_NONE}
        </button>
      </div>

      {fillOpen ? (
        <div className="cc-cal-fill">
          <div className="cc-cal-fill-col">
            <span className="cc-cal-label">{T.PICK_VIDEOS}</span>
            <input
              id="cc-cal-search"
              className="cc-cal-search"
              placeholder={T.SEARCH_VIDEOS}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="cc-cal-videos">
              {shownVideos.map((v) => (
                <label key={v.id} className={`cc-cal-video${selected.has(v.id) ? " is-on" : ""}`}>
                  <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggle(v.id)} />
                  <span className="cc-cal-video-title">{v.title}</span>
                  <span className="cc-cal-video-meta">
                    {T.clips(v.clips_count)}
                    {readScores(v.id) ? ` · ${T.AI_SCORED}` : ""}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="cc-cal-fill-col">
            <span className="cc-cal-label">{T.HOW}</span>
            <div className="cc-cal-pills" role="group" aria-label={T.HOW}>
              {data.ai?.available ? (
                <button type="button" aria-pressed={picker === "ai"} onClick={() => setPicker("ai")}>{T.aiOnUs(data.ai.left)}</button>
              ) : null}
              <button type="button" aria-pressed={picker === "template"} onClick={() => setPicker("template")}>{T.TEMPLATES}</button>
              <button type="button" aria-pressed={picker === "order"} onClick={() => setPicker("order")}>{T.AI}</button>
            </div>
            {picker === "template" ? (
              <div className="cc-cal-pills" role="group" aria-label={T.TEMPLATES}>
                {CALENDAR_TEMPLATES.map((r) => (
                  <button key={r.key} type="button" aria-pressed={recipe === r.key} onClick={() => setRecipe(r.key)}>
                    {r.label}
                  </button>
                ))}
              </div>
            ) : picker === "ai" ? (
              <p className={data.ai?.left === 0 ? "cc-cal-warning" : "cc-cal-note"}>
                {data.ai?.left === 0 ? T.AI_ON_US_NONE : T.AI_ON_US_NOTE}
              </p>
            ) : (
              <>
                <p className="cc-cal-note">{T.AI_NOTE}</p>
                {missingScores.map((v) => (
                  <p key={v.id} className="cc-cal-warning">
                    {T.aiMissing(v.title)}{" "}
                    <a href={`/create-video/?t=${v.id}`} target="_blank" rel="noopener noreferrer">{T.OPEN_TO_SCORE}</a>
                  </p>
                ))}
              </>
            )}
            <span className="cc-cal-label">{T.HOW_FAR}</span>
            <div className="cc-cal-pills" role="group" aria-label={T.HOW_FAR}>
              {T.AHEAD.map((a) => (
                <button key={a.days} type="button" aria-pressed={ahead === a.days} onClick={() => setAhead(a.days)}>{a.label}</button>
              ))}
            </div>
            <div className="cc-cal-actions">
              <button
                type="button"
                className="cc-cal-primary"
                disabled={booking || !emptyWithin(ahead) || (picker === "ai" && (data.ai?.left === 0 || aiRunning))}
                onClick={doFill}
              >
                {booking ? T.BOOKING : picker === "ai" ? T.bookAi(emptyWithin(ahead)) : T.book(emptyWithin(ahead))}
              </button>
              <button type="button" className="cc-cal-ghost" onClick={() => setFillOpen(false)}>{T.CANCEL}</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="cc-cal-days">
        {days.map((day) => (
          <div key={day.key} className="cc-cal-day">
            <div className="cc-cal-day-label">{day.label}</div>
            <div className="cc-cal-cells">
              {day.cells.map(({ at, post }) => (
                <div
                  key={at.getTime()}
                  className={`cc-cal-cell${post ? ` is-${post.state} has-clip` : " is-empty"}`}
                  role={post ? "button" : undefined}
                  tabIndex={post ? 0 : undefined}
                  aria-label={post ? `${timeLabel(at)} ${post.text}` : undefined}
                  onClick={post ? () => setOpened(post) : undefined}
                  onKeyDown={post ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpened(post); } } : undefined}
                >
                  {post ? (
                    <span className="cc-cal-poster" title={post.video ? T.PLAY_TITLE : T.NOT_CUT_YET}>
                      {post.thumbnail ? <img src={post.thumbnail} alt="" /> : <span className="cc-cal-poster-blank" />}
                      {post.video ? <span className="cc-cal-poster-play">▶</span> : null}
                    </span>
                  ) : null}
                  <div className="cc-cal-cell-body">
                    <div className="cc-cal-cell-top">
                      <span className="cc-cal-cell-time">{timeLabel(at)}</span>
                      {post ? (
                        <span className="cc-cal-state" title={post.error || undefined}>{T.STATE[post.state] || post.state}</span>
                      ) : (
                        <span className="cc-cal-state">{T.EMPTY}</span>
                      )}
                    </div>
                    {post ? <div className="cc-cal-cell-text">{post.text}</div> : null}
                    {post && post.title ? <div className="cc-cal-cell-from">{T.FROM} {post.title}</div> : null}
                  </div>
                  {post && (post.state === "rendering" || post.state === "ready") ? (
                    <button type="button" className="cc-cal-skip" title={T.SKIP} aria-label={T.SKIP} onClick={(e) => { e.stopPropagation(); doSkip(post); }}>
                      ×
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={Boolean(opened)} onClose={() => setOpened(null)} PaperProps={{ className: "cc-cal-details" }}>
        {opened ? (
          <div className="cc-cal-details-body" role="group" aria-label={T.DETAILS_TITLE}>
            <div className="cc-cal-details-media">
              {opened.video ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video src={opened.video} poster={opened.thumbnail || undefined} controls autoPlay playsInline />
              ) : opened.thumbnail ? (
                <img src={opened.thumbnail} alt="" />
              ) : (
                <span className="cc-cal-poster-blank" />
              )}
            </div>
            <div className="cc-cal-details-info">
              <span className="cc-cal-state">{T.STATE[opened.state] || opened.state}</span>
              <p className="cc-cal-details-text">{opened.text}</p>
              {opened.title ? <p className="cc-cal-cell-from">{T.FROM} {opened.title}</p> : null}
              <p className="cc-cal-note">{T.postsAt(`${dayLabel(new Date(opened.post_at))} ${timeLabel(new Date(opened.post_at))}`)}</p>
              {!opened.video ? <p className="cc-cal-note">{T.WAITING_CUT}</p> : null}
              {opened.error ? <p className="cc-cal-warning">{opened.error}</p> : null}
              <div className="cc-cal-actions">
                {opened.state === "rendering" || opened.state === "ready" ? (
                  <button type="button" className="cc-cal-ghost" onClick={() => doSkip(opened)}>{T.TAKE_OFF}</button>
                ) : null}
                <button type="button" className="cc-cal-ghost" onClick={() => setOpened(null)}>{T.CLOSE}</button>
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default CalendarTab;
